<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import router from "@/router/index.js";
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import EntityForm from "@/components/common/Form.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import { handleError } from "@/utils/errors.js";
import { computeClass } from "@/utils/styleFormats.js";
import { buildPayloadFromEntity } from "@/utils/index.js";
import { ApplicationEntity } from "@/entities/ApplicationEntity.js";
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import { listCorps } from "@/services/api/corporation.js";
import {
    deleteApplicationUrl,
    getApplicationById,
    getApplicationUrls,
    listServices,
    saveApplication,
    saveApplicationUrl,
} from "@/services/api/applications.js";

const { t } = useI18n();
const route = useRoute();
const id = route.params.id;
const loading = ref(false);
const isMounted = ref(false);
const isFetching = ref(false);
const form = ref(null);
const entity = ref(new ApplicationEntity());
const formData = ref({
    ...entity.value.getDefaultValues(),
    visible_in_menu: true,
    sequence: 0,
    status: true,
});

/**
 * Enderecos da aplicacao. So existem depois que ela existe -- por isso a seção
 * aparece apenas na edicao. Sem pelo menos uma URL que sirva ao ambiente, a
 * aplicacao fica fora do menu: `GET /apps` omite quem nao tem destino
 * resolvido, em vez de devolver item que nao abre.
 */
const urls = ref([]);
const carregandoUrls = ref(false);
const dialogUrl = ref(false);
const salvandoUrl = ref(false);
const formUrl = ref(null);
const corporacoes = ref([]);

/**
 * As opcoes do seletor de empresa, memorizadas.
 *
 * Estava como array literal no `:items` do template. Um literal e recriado a
 * cada render, e escolher uma opcao **provoca** um render -- entao a lista
 * trocava de identidade no meio da selecao e o `v-select` a descartava: o
 * campo voltava para "Todas as empresas" e o `corp_id` seguia nulo. O efeito
 * era nao conseguir vincular endereco a empresa nenhuma, sem erro na tela.
 *
 * Com `computed` a referencia so muda quando `corporacoes` muda de verdade.
 */
const opcoesDeCorporacao = computed(() => [
    { id: null, companyName: t("components.settings.applications.todasCorporacoes") },
    ...corporacoes.value,
]);
const urlEmEdicao = ref(null);
const dialogExcluirUrl = ref(false);
const urlParaExcluir = ref(null);

const urlVazia = () => ({ id: null, url: "", environment: "", corp_id: null, is_default: false, status: true });

/**
 * APIs com que esta aplicacao pode falar. A lista viaja no token (claim `apis`)
 * e cada API confere se o proprio nome esta nela -- e o que impede o token do
 * accounts de abrir o contract.
 *
 * Vazio significa "sem restricao declarada": o token sai sem a claim, e as APIs
 * so recusam quando a exigencia estiver ligada nelas.
 */
const apisSelecionadas = ref([]);
const apisSugeridas = ref([]);

/**
 * Aparece no menu. Fica fora da entity porque o formulario generico nao tem
 * switch: o `buildPayloadFromEntity` so devolve booleano para `activeSwitch`,
 * e a API recusa a string "true".
 */
const apareceNoMenu = ref(true);

/** So a origem: a API recusa caminho, query ou fragmento. */
function validarOrigem(valor) {
    if (!valor) return t("components.settings.applications.urlObrigatoria");

    try {
        const endereco = new URL(valor);
        const soOrigem = (endereco.pathname === "/" || endereco.pathname === "")
            && !endereco.search && !endereco.hash && !endereco.username;

        if (!["http:", "https:"].includes(endereco.protocol) || !soOrigem) {
            return t("components.settings.applications.urlInvalida");
        }
    } catch {
        return t("components.settings.applications.urlInvalida");
    }

    return true;
}

async function carregarUrls() {
    if (!id) return;

    try {
        carregandoUrls.value = true;
        urls.value = await getApplicationUrls({ id });
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        carregandoUrls.value = false;
    }
}

function novaUrl() {
    urlEmEdicao.value = urlVazia();
    dialogUrl.value = true;
}

function editarUrl(url) {
    urlEmEdicao.value = { ...url };
    dialogUrl.value = true;
}

async function salvarUrl() {
    const { valid } = await formUrl.value.validate();
    if (!valid) return;

    try {
        salvandoUrl.value = true;
        await saveApplicationUrl({ id, url: urlEmEdicao.value });
        dialogUrl.value = false;
        await carregarUrls();
        message.success(t("common.messages.success.save"));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        salvandoUrl.value = false;
    }
}

function confirmarExclusaoUrl(url) {
    urlParaExcluir.value = url;
    dialogExcluirUrl.value = true;
}

async function excluirUrl() {
    try {
        await deleteApplicationUrl({ id, urlId: urlParaExcluir.value.id });
        dialogExcluirUrl.value = false;
        await carregarUrls();
        message.success(t("common.messages.success.itemDeletedSuccessfully"));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    }
}

function nomeDaCorporacao(corpId) {
    if (!corpId) return t("components.settings.applications.todasCorporacoes");
    return corporacoes.value.find(c => c.id === corpId)?.companyName ?? corpId;
}

/**
 * Servicos disponiveis para o campo que decide quem enxerga a aplicacao.
 *
 * Nao usa o `searchAndSetOptions` de propósito: ele esvazia as opcoes quando a
 * busca vem vazia, e aqui a carga inicial importa -- editando uma aplicacao que
 * ja tem servico, o campo precisa mostrar o nome, e nao o uuid guardado.
 */
async function carregarServicos(termo = null) {
    const campo = entity.value.fields.find(c => c.key === "service_id");
    if (!campo) return;

    try {
        isFetching.value = true;
        const servicos = await listServices(termo ? { name: termo } : {});
        campo.options = (servicos ?? []).map(servico => ({
            value: servico.id,
            label: servico.name,
        }));

        // Os mesmos nomes servem de sugestao para a lista de APIs: e assim que
        // os servicos nascem do makePermissionStructure (hliberty.auth, ...).
        if (!termo) {
            apisSugeridas.value = (servicos ?? []).map(servico => servico.name).filter(Boolean).sort();
        }
    } catch (error) {
        // Sem a lista o campo fica vazio, e a aplicacao nasce sem vinculo --
        // visivel a todos. E recuperavel editando depois.
        console.warn("Nao foi possivel listar os servicos:", error);
        campo.options = [];
    } finally {
        isFetching.value = false;
    }
}

/**
 * Mostra o nome do servico ja vinculado, e nao o uuid guardado.
 *
 * O `Autocomplete.vue` e generico e trabalha com `return-object`: o rotulo que
 * ele exibe sai do proprio valor do modelo, nao de uma busca na lista -- que
 * alias so e montada depois de tres caracteres digitados. Entao um
 * `service_id` vindo da API como string nunca achava rotulo, e o campo abria
 * em branco mesmo com servico vinculado, que se le como "sem servico".
 *
 * Trocar a string pela opcao correspondente resolve a exibicao sem tocar no
 * componente compartilhado: ao salvar, o `buildPayloadFromEntity` desembrulha
 * o `{ value }` de volta para o uuid.
 */
function resolverServicoSelecionado() {
    const atual = formData.value?.service_id;
    if (!atual || typeof atual === "object") return;

    const campo = entity.value.fields.find(c => c.key === "service_id");
    const opcao = campo?.options?.find(o => o.value === atual);

    // Sem a opcao na lista (servico fora das 50 primeiras, por exemplo), o uuid
    // ao menos deixa visivel que o vinculo existe.
    formData.value.service_id = opcao
        ? { value: opcao.value, label: opcao.label, title: opcao.label }
        : { value: atual, label: atual, title: atual };
}

async function handleSearch({ handler, query }) {
    if (handler === "handleServiceList") {
        await carregarServicos(query);
    }
}

async function onSave() {
    try {
        const { valid } = await form.value.validate();
        if (!valid) return;

        loading.value = true;

        const payload = buildPayloadFromEntity({ entity: entity.value, data: formData.value });
        payload.id = id ?? null;

        // O formulario nao tem como saber a diferenca entre "sem servico" e
        // "campo vazio": a API trata os dois como sem vinculo.
        if (!payload.service_id) {
            payload.service_id = null;
        }

        // Lista vazia e "sem restricao declarada", nao "lista vazia": a API
        // trata null assim, e mandar [] faria a aplicacao nao poder falar com
        // nenhuma API quando a exigencia estivesse ligada.
        payload.apis = apisSelecionadas.value?.length ? apisSelecionadas.value : null;

        // Booleano de verdade: a API valida `boolean`, que aceita true/1/"1"
        // mas nao a string "true".
        payload.visible_in_menu = !!apareceNoMenu.value;

        await saveApplication(payload);
        await router.push(entity.value.pages.list.route?.at(-1));
        message.success(t("common.messages.success.save"));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t, formData?.value?.slug));
    } finally {
        loading.value = false;
    }
}

onMounted(async () => {
    try {
        isMounted.value = false;

        // O slug identifica a aplicacao nos tokens ja emitidos para ela, e a
        // API recusa troca. Editando, o campo fica visivel mas travado.
        if (id) {
            const campoSlug = entity.value.fields.find(campo => campo.key === "slug");
            if (campoSlug) {
                campoSlug.readOnly = true;
                campoSlug.hint = "components.settings.applications.slugImutavel";
            }

            const response = await getApplicationById({ id });
            formData.value = response.data ?? response;
            apisSelecionadas.value = formData.value.apis ?? [];
            apareceNoMenu.value = formData.value.visible_in_menu ?? true;
        }

        await carregarServicos();

        if (id) {
            resolverServicoSelecionado();
            corporacoes.value = await listCorps({}) ?? [];
            await carregarUrls();
        }
    } catch (error) {
        console.error("Erro ao montar o componente:", error);
        message.error(await handleError(error, t));
    } finally {
        isMounted.value = true;
    }
});
</script>

<template>
    <BasePage :loading-mounted="isMounted" :entity="entity">
        <template #default>
            <EntityForm :entity="entity" v-model="formData" ref="form" @search="handleSearch" />
            <ButtonSave :loading="loading" @click="onSave" />

            <v-card class="mt-6" elevation="0" border>
                <v-card-title>{{ t("components.settings.applications.exibicao") }}</v-card-title>
                <v-card-text>
                    <v-switch
                        v-model="apareceNoMenu"
                        :label="t('components.settings.applications.visibleInMenu')"
                        :hint="t('components.settings.applications.visibleInMenuHint')"
                        persistent-hint
                        color="primary"
                    />
                </v-card-text>
            </v-card>

            <v-card class="mt-6" elevation="0" border>
                <v-card-title>{{ t("components.settings.applications.apis") }}</v-card-title>
                <v-card-subtitle class="pb-3">
                    {{ t("components.settings.applications.apisDescricao") }}
                </v-card-subtitle>
                <v-card-text>
                    <v-combobox
                        v-model="apisSelecionadas"
                        :items="apisSugeridas"
                        :label="t('components.settings.applications.apis')"
                        :hint="t('components.settings.applications.apisHint')"
                        persistent-hint
                        multiple
                        chips
                        closable-chips
                        clearable
                    />
                </v-card-text>
            </v-card>

            <!--
                Enderecos: so na edicao, porque dependem da aplicacao existir.
                Sem uma URL que sirva ao ambiente, a aplicacao nao aparece no
                menu -- o servidor omite quem nao tem destino resolvido.
            -->
            <v-card v-if="id" class="mt-6" elevation="0" border>
                <v-card-title class="d-flex align-center justify-space-between">
                    <span>{{ t("components.settings.applications.urls") }}</span>
                    <v-btn color="primary" variant="flat" size="small" @click="novaUrl">
                        {{ t("components.settings.applications.novaUrl") }}
                    </v-btn>
                </v-card-title>

                <v-card-subtitle class="pb-3">
                    {{ t("components.settings.applications.urlsDescricao") }}
                </v-card-subtitle>

                <v-card-text>
                    <v-progress-linear v-if="carregandoUrls" indeterminate color="primary" class="mb-3" />

                    <p v-if="!carregandoUrls && !urls.length" class="text-medium-emphasis">
                        {{ t("components.settings.applications.semUrls") }}
                    </p>

                    <v-table v-else-if="!carregandoUrls" density="comfortable">
                        <thead>
                            <tr>
                                <th>{{ t("components.settings.applications.url") }}</th>
                                <th>{{ t("components.settings.applications.environment") }}</th>
                                <th>{{ t("components.settings.applications.corporation") }}</th>
                                <th>{{ t("components.settings.applications.isDefault") }}</th>
                                <th>{{ t("common.labels.status") }}</th>
                                <th class="text-right">{{ t("common.labels.actions") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="url in urls" :key="url.id">
                                <td>{{ url.url }}</td>
                                <td>{{ url.environment || t("components.settings.applications.todosAmbientes") }}</td>
                                <td>{{ nomeDaCorporacao(url.corp_id) }}</td>
                                <td>
                                    <span class="material-symbols-outlined" v-if="url.is_default">check</span>
                                </td>
                                <td>
                                    <span class="trezo-status-badge" :class="computeClass(url.status)">
                                        {{ t('common.labels.' + (url.status ? 'active' : 'inactive')) }}
                                    </span>
                                </td>
                                <td class="text-right">
                                    <v-btn icon variant="text" size="small" @click="editarUrl(url)">
                                        <span class="material-symbols-outlined">edit</span>
                                    </v-btn>
                                    <v-btn icon variant="text" size="small" @click="confirmarExclusaoUrl(url)">
                                        <span class="material-symbols-outlined">delete</span>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>

            <v-dialog v-model="dialogUrl" max-width="640">
                <v-card v-if="urlEmEdicao">
                    <v-card-title>
                        {{ urlEmEdicao.id
                            ? t("components.settings.applications.editarUrl")
                            : t("components.settings.applications.novaUrl") }}
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="formUrl">
                            <v-text-field
                                v-model="urlEmEdicao.url"
                                :label="t('components.settings.applications.url')"
                                :hint="t('components.settings.applications.urlHint')"
                                persistent-hint
                                :rules="[validarOrigem]"
                                class="mb-4"
                            />
                            <v-text-field
                                v-model="urlEmEdicao.environment"
                                :label="t('components.settings.applications.environment')"
                                :hint="t('components.settings.applications.environmentHint')"
                                persistent-hint
                                class="mb-4"
                            />
                            <v-select
                                v-model="urlEmEdicao.corp_id"
                                :items="opcoesDeCorporacao"
                                item-title="companyName"
                                item-value="id"
                                :label="t('components.settings.applications.corporation')"
                                :hint="t('components.settings.applications.corporationHint')"
                                persistent-hint
                                class="mb-4"
                            />
                            <v-switch
                                v-model="urlEmEdicao.is_default"
                                :label="t('components.settings.applications.isDefault')"
                                color="primary"
                                hide-details
                            />
                            <v-switch
                                v-model="urlEmEdicao.status"
                                :label="t('common.labels.active')"
                                color="primary"
                                hide-details
                            />
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn variant="text" @click="dialogUrl = false">
                            {{ t("common.labels.cancel") }}
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="salvandoUrl" @click="salvarUrl">
                            {{ t("common.labels.save") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <DialogConfirmDelet
                v-model="dialogExcluirUrl"
                :item="urlParaExcluir?.url"
                @confirm="excluirUrl"
            />
        </template>
    </BasePage>
</template>
